import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Section, Cell, Caption, Button, Textarea, Badge } from '@telegram-apps/telegram-ui'
import { CheckCircle2, AlertTriangle, Circle, Briefcase, Home, Check } from 'lucide-react'

import { api } from '../../lib/api'
import type { Task, Comment } from '../../lib/types'
import { formatDeadline } from '../../lib/format'
import BackHeader from '../../components/BackHeader'
import { SkeletonList } from '../../components/Skeleton'
import { hapticSuccess } from '../../lib/telegram'

interface TaskDetailResp {
  task: Task
  comments: Comment[]
}

export default function DirectorTaskDetail() {
  const { id } = useParams<{ id: string }>()
  const taskId = Number(id)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [comment, setComment] = useState('')

  const { data, isLoading } = useQuery<TaskDetailResp>({
    queryKey: ['task', taskId],
    queryFn: () => api.get<TaskDetailResp>(`/tasks/${taskId}`),
  })

  const myId = qc.getQueryData<{ user_id: number }>(['me'])?.user_id

  const markDone = useMutation({
    mutationFn: () => api.post(`/tasks/${taskId}/done`),
    onSuccess: () => {
      hapticSuccess()
      qc.invalidateQueries({ queryKey: ['task', taskId] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      navigate(-1)
    },
  })

  const cancel = useMutation({
    mutationFn: () => api.del(`/tasks/${taskId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      navigate(-1)
    },
  })

  const addComment = useMutation({
    mutationFn: () => api.post(`/tasks/${taskId}/comments`, { content: comment }),
    onSuccess: () => {
      setComment('')
      qc.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })

  if (isLoading || !data) {
    return (
      <>
        <BackHeader title="Задача" />
        <SkeletonList count={4} />
      </>
    )
  }

  const t = data.task
  const isMyTask = t.assignee_id === myId
  const canEdit = t.assigner_id === myId
  const isDone = t.status === 'done'
  const badgeMode = t.category === 'work' ? 'primary' : 'gray'

  return (
    <>
      <BackHeader title={t.title} />

      <Section>
        <Cell
          before={isDone
            ? <CheckCircle2 size={20} color="var(--success)" />
            : t.deadline && new Date(t.deadline) < new Date(new Date().toDateString())
              ? <AlertTriangle size={20} color="var(--danger)" />
              : <Circle size={20} color="var(--warning)" />}
          subtitle={<Caption>{isDone ? 'Выполнена' : formatDeadline(t.deadline)}</Caption>}
        >
          <Badge type="number" mode={badgeMode as any}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {t.category === 'work' ? <Briefcase size={12} /> : <Home size={12} />}
              {t.category === 'work' ? 'Рабочая' : 'Личная'}
            </span>
          </Badge>
        </Cell>

        {t.description && (
          <Cell multiline subtitle="Описание">
            {t.description}
          </Cell>
        )}

        <Cell subtitle="Исполнитель">
          {isMyTask ? 'Я' : 'Анастасия'}
        </Cell>
      </Section>

      <Section header={`Комментарии (${data.comments.length})`}>
        {data.comments.length === 0 ? (
          <Cell><Caption style={{ opacity: 0.6 }}>Пока нет</Caption></Cell>
        ) : (
          data.comments.map((c) => (
            <Cell
              key={c.id}
              subtitle={<Caption>{c.author_role === 'director' ? 'Я' : 'Анастасия'} · {new Date(c.created_at).toLocaleString('ru-RU')}</Caption>}
              multiline
            >
              {c.content}
            </Cell>
          ))
        )}

        <div style={{ padding: '12px 16px' }}>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Добавить комментарий..."
          />
          <div style={{ marginTop: 8 }}>
            <Button
              stretched
              disabled={!comment.trim() || addComment.isPending}
              onClick={() => addComment.mutate()}
            >
              Отправить
            </Button>
          </div>
        </div>
      </Section>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!isDone && isMyTask && (
          <Button stretched size="l" onClick={() => markDone.mutate()} before={<Check size={18} />}>
            Готово
          </Button>
        )}
        {canEdit && !isDone && (
          <Button stretched size="l" mode="plain" onClick={() => cancel.mutate()}>
            🗑 Отменить задачу
          </Button>
        )}
      </div>
    </>
  )
}
